import React, { Fragment, useId } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { ProgressBar } from '@/components/ui/ProgressBar';
import type { BillingExportJob, StatementExportFormat } from '@/types';

const { Button: MenuButton, Items: MenuItems, Item: MenuItem } = Menu;

/** One selectable download - a format of the document currently on screen. */
export interface DownloadOption {
  format: StatementExportFormat;
  label: string;
  onSelect: () => void;
}

interface Props {
  options: DownloadOption[];
  isDownloading: boolean;
  /** The background export being built (LR-728); null while there is none,
   *  or while its finished file is being fetched. */
  exportJob?: BillingExportJob | null;
  /** The running export is of ANOTHER document than the one on screen: the
   *  menu stays locked, but this document's trigger must not wear its progress. */
  busyElsewhere?: boolean;
  ariaLabel?: string;
}

// Share of the document written, or null when there is nothing to count: the
// export is queued, the document not yet priced, or the PDF is being laid out.
const progressFraction = (job?: BillingExportJob | null) =>
  job?.progress?.phase === 'building' && job.progress.total
    ? job.progress.done / job.progress.total
    : null;

const busyLabel = (job: BillingExportJob | null, elsewhere: boolean) => {
  if (elsewhere) return 'Another export...';
  const fraction = progressFraction(job);
  if (fraction !== null) return `Preparing ${Math.round(fraction * 100)}%`;
  if (job?.progress?.phase === 'rendering') return 'Rendering...';
  if (job?.status === 'queued') return 'Queued...';
  if (job) return 'Preparing...';
  return 'Downloading...';
};

// What the export is doing, by stage only - announced when it changes, so a
// screen reader hears the steps of a long export rather than every percent.
const stageText = (job: BillingExportJob | null, elsewhere: boolean) => {
  if (elsewhere) return 'Another document is being exported';
  if (job?.progress?.phase === 'rendering') return 'Laying out the PDF';
  if (job?.status === 'queued') return 'Export queued';
  if (job) return 'Preparing the file';
  return 'Downloading the file';
};

const rowsText = (job: BillingExportJob | null) => {
  const progress = job?.progress;
  if (progressFraction(job) === null || !progress?.total) return undefined;
  return `${progress.done.toLocaleString('en-US')} of ${progress.total.toLocaleString('en-US')} rows`;
};

/**
 * Download-format picker shared by the statement detail and the MTD preview
 * (LR-553). One component rather than two copies: the focus and busy handling
 * below is subtle enough that a second copy would drift from it.
 *
 * Deliberately just a flat list of formats. Since LR-553 both callers show ONE
 * document, so there is never a second statement to disambiguate between - the
 * grouping machinery an earlier draft carried had no consumer left.
 */
export const DownloadMenu: React.FC<Props> = ({
  options,
  isDownloading,
  exportJob = null,
  busyElsewhere = false,
  ariaLabel = 'Download statement',
}) => {
  const barId = useId();
  const label = busyLabel(exportJob, busyElsewhere);
  const showBar = isDownloading && !busyElsewhere;
  return (
    <Menu as="div" className="relative">
      {/* Not `disabled` while downloading: headlessui restores focus to the
          trigger on `nextFrame` after a menu-item click, but a disabled element
          can't take focus — it would land on <body> (WCAG 2.4.3). Keep it
          focusable, signal busy via aria, and gate re-entry in the item handler.
          While busy the name carries the visible text too (WCAG 2.5.3), and the
          fixed minimum width keeps the header still as the percentage ticks. */}
      <MenuButton
        className={`btn btn-secondary btn-sm inline-flex items-center gap-1 tabular-nums ${
          isDownloading
            ? 'min-w-[9.5rem] cursor-not-allowed justify-center opacity-50'
            : ''
        }`}
        aria-busy={isDownloading}
        aria-disabled={isDownloading}
        aria-label={isDownloading ? `${ariaLabel}, ${label}` : ariaLabel}
        aria-describedby={showBar ? barId : undefined}
      >
        {isDownloading ? label : 'Download'}
        <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
      </MenuButton>
      <span className="sr-only" role="status">
        {isDownloading ? stageText(exportJob, busyElsewhere) : ''}
      </span>
      {/* A large document takes a while to build (LR-728); the bar is what says
          it is moving. Hung under the trigger, out of the header's flow, so the
          row does not jump when an export starts. */}
      {showBar && (
        <ProgressBar
          id={barId}
          value={progressFraction(exportJob)}
          label="Export progress"
          valueText={rowsText(exportJob) ?? stageText(exportJob, false)}
          className="absolute inset-x-0 top-full mt-1"
        />
      )}
      {/* motion-safe: respect prefers-reduced-motion (a11y). */}
      <Transition
        as={Fragment}
        enter="motion-safe:transition motion-safe:ease-out motion-safe:duration-100"
        enterFrom="transform motion-safe:opacity-0 motion-safe:scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="motion-safe:transition motion-safe:ease-in motion-safe:duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform motion-safe:opacity-0 motion-safe:scale-95"
      >
        <MenuItems className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-[10px] bg-lr-surface py-1.5 shadow-lg ring-1 ring-lr-border focus:outline-none">
          {options.map((option) => (
            <MenuItem key={option.format}>
              {({ active }) => (
                <button
                  type="button"
                  // One download at a time (a single shared flag). Say so
                  // rather than swallowing the click: with several forming
                  // statements a trader will start the second while the first
                  // is still running, and a handler that just returns leaves
                  // no toast, no spinner change and nothing attributable to
                  // their click - the menu simply looks broken. `aria-disabled`
                  // over `disabled` for the same focus-restore reason as the
                  // trigger above.
                  aria-disabled={isDownloading}
                  onClick={() => {
                    // Gate re-entry here (item stays focusable).
                    if (!isDownloading) option.onSelect();
                  }}
                  className={`${active ? 'bg-lr-surface-2' : ''} ${
                    isDownloading ? 'cursor-not-allowed opacity-50' : ''
                  } flex w-full items-center px-3 py-2 text-left text-[13px] text-lr-fg-2`}
                >
                  {option.label}
                </button>
              )}
            </MenuItem>
          ))}
        </MenuItems>
      </Transition>
    </Menu>
  );
};
